import React, { useState, useEffect } from 'react';
import { getStatusOptions, getOwnershipOptions } from '../../utils/statusOptions';
import Modal from '../Modal';

const QuickEditDetails = ({
  mediaItem,
  editedMedia,
  editMode,
  canEdit,
  handleInputChange,
  handleIncrement,
  apiBaseUrl,
  onRefreshMedia,
}) => {
  // Determine if media is Anime with multiple seasons
  const hasEditableSeasons =
    ['Anime', 'AnimeMovie', 'AnimeSerie'].includes(mediaItem?.type) ||
    (mediaItem?.type === 'ScreenMedia' && mediaItem.media_format?.toLowerCase() === 'tv');


  // Auto-select season logic
  // Bonus/extra seasons (season.is_extra) never drive the "current" season and never block
  // the whole item from counting as finished/plan-to-watch - they're optional side content.
  const getMainSeasons = () => (mediaItem.seasons || []).filter((s) => !s.is_extra);

  const getInitialSeason = () => {
    if (!hasEditableSeasons) return null;
    const mainSeasons = getMainSeasons();
    if (mainSeasons.length === 0) return mediaItem.seasons[0];
    let watchingSeason = mainSeasons.find((s) => s.status === 'Watching');
    if (watchingSeason) return watchingSeason;
    let notFinishedOrPlan = mainSeasons.find(
      (s) => s.status !== 'Finished' && s.status !== 'Plan to Watch'
    );
    if (notFinishedOrPlan) return notFinishedOrPlan;
    let allPlanToWatch = mainSeasons.every((s) => s.status === 'Plan to Watch');
    if (allPlanToWatch) return mainSeasons[0];
    let allFinished = mainSeasons.every((s) => s.status === 'Finished');
    if (allFinished) return mainSeasons[mainSeasons.length - 1];
    return mainSeasons[0];
  };

  const [selectedSeason, setSelectedSeason] = useState(getInitialSeason);

  // Local/global manual rank editing state
  const [localRankInput, setLocalRankInput] = useState('');
  const [globalRankInput, setGlobalRankInput] = useState('');
  const [rankConflict, setRankConflict] = useState(null); // { kind: 'local'|'global', targetPosition, suggestionKind, suggestedValue, neighborTitle }
  const [rankSaving, setRankSaving] = useState(false);

  useEffect(() => {
    setLocalRankInput(mediaItem?.local_rank ?? '');
    setGlobalRankInput(mediaItem?.global_rank ?? '');
  }, [mediaItem?.local_rank, mediaItem?.global_rank]);

  const submitRank = (kind, targetPosition, confirm = false) => {
    if (!mediaItem?.id || !targetPosition || Number.isNaN(targetPosition)) return;
    setRankSaving(true);
    fetch(`${apiBaseUrl}/rank/${kind}/${mediaItem.id}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ target_position: targetPosition, confirm }),
    })
      .then((res) => res.json())
      .then((data) => {
        setRankSaving(false);
        if (data.conflict) {
          setRankConflict({
            kind,
            targetPosition,
            suggestionKind: data.suggestion_kind,
            suggestedValue: data.suggested_value,
            neighborTitle: data.neighbor_title,
          });
          return;
        }
        if (data.success) {
          onRefreshMedia?.();
        } else {
          alert('Failed to update rank: ' + (data.error || 'Unknown error'));
          setLocalRankInput(mediaItem?.local_rank ?? '');
          setGlobalRankInput(mediaItem?.global_rank ?? '');
        }
      })
      .catch((err) => {
        setRankSaving(false);
        alert('Error updating rank: ' + err.message);
      });
  };

  const acceptRankConflict = () => {
    if (!rankConflict) return;
    const { kind, targetPosition } = rankConflict;
    setRankConflict(null);
    submitRank(kind, targetPosition, true);
  };

  const declineRankConflict = () => {
    setRankConflict(null);
    setLocalRankInput(mediaItem?.local_rank ?? '');
    setGlobalRankInput(mediaItem?.global_rank ?? '');
  };

  // Guard clause to prevent rendering until mediaItem and editedMedia are available
  if (!mediaItem || !editedMedia) {
    return null;
  }

  const canModify = canEdit; // Always editable if canEdit is true
  const currentData = hasEditableSeasons ? selectedSeason : editedMedia;

  const handleSeasonChange = (e) => {
    const seasonId = parseInt(e.target.value);
    const season = mediaItem.seasons.find((s) => s.id === seasonId);
    setSelectedSeason(season);
  };

  // Numbers seasons using only the non-extra seasons, so bonus/extra entries don't shift the
  // "Season N" numbering of the real seasons around them.
  const getSeasonLabel = (season) => {
    if (season.is_extra) {
      return season.title_tmdb || season.title_en || season.title_mal || `${mediaItem.title} Bonus Content`;
    }
    if (season.title_tmdb && season.title_tmdb !== mediaItem.title) {
      return season.title_tmdb;
    }
    const seasonNumber = getMainSeasons().indexOf(season) + 1;
    return `${mediaItem.title} Season ${seasonNumber}`;
  };

  const handleFieldChange = (field, value, isSeasonField = false) => {
    if (!canModify) return;
    const targetData = isSeasonField && hasEditableSeasons ? selectedSeason : editedMedia;
    const targetId = isSeasonField && hasEditableSeasons ? selectedSeason?.id : mediaItem.id;
    const fieldToSend = isSeasonField && field === 'score' ? 'season_score' : field; // Map 'score' to 'season_score' for seasons
    if (isSeasonField && hasEditableSeasons) {
      fetch(`${apiBaseUrl}/update_season_setting/${targetId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ field: fieldToSend, value, media_type: mediaItem.type }),
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
          return res.json();
        })
        .then((data) => {
          if (data.success) {
            setSelectedSeason((prev) => ({ ...prev, [field]: value }));
            mediaItem.seasons = mediaItem.seasons.map((s) =>
              s.id === targetId ? { ...s, [field]: value } : s
            );
          } else {
            console.error('Update failed:', data);
            alert('Failed to save changes: ' + (data.message || 'Unknown error'));
          }
        })
        .catch((err) => {
          console.error('Fetch error:', err);
          alert('Error saving changes: ' + err.message);
        });
    } else {
      handleInputChange(field, value);
    }
  };

  const handleIncrementField = (field, delta, isSeasonField = false) => {
    if (!canModify) return;
    const targetData = isSeasonField && hasEditableSeasons ? selectedSeason : editedMedia;
    const currentValue = parseInt(targetData[field], 10) || 0;
    const newValue = Math.max(0, currentValue + delta);
    handleFieldChange(field, newValue, isSeasonField);
  };

  return (
    <>
    <section className="quick-edit-details">
      {hasEditableSeasons && (
        <div className="quick-edit-row">
          <label>Season:</label>
          {canModify ? (
            <select
              value={selectedSeason?.id || ''}
              onChange={handleSeasonChange}
            >
              {mediaItem.seasons.map((season) => (
                <option key={season.id} value={season.id}>
                {getSeasonLabel(season)}
              </option>
              
              ))}
            </select>
          ) : (
            <span>{selectedSeason?.title_en || selectedSeason?.title_mal || `Season ${selectedSeason?.id}`}</span>
          )}
        </div>
      )}

      {/* Status */}
      <div className="quick-edit-row">
        <label>Status:</label>
        {canModify ? (
          <select
            value={currentData.status || ''}
            onChange={(e) => handleFieldChange('status', e.target.value, hasEditableSeasons)}
          >
            {getStatusOptions(mediaItem.type).map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        ) : (
          <span>{currentData.status || 'Not Set'}</span>
        )}
      </div>

      {/* Current Episode for Anime, Current Chapter and Volume for Manga, Current Page for Book */}
      {(mediaItem.type === 'Manga' ||
        mediaItem.type === 'Book' ||
        hasEditableSeasons) && (
        <>
          {mediaItem.type === 'Manga' ? (
            <>
              <div className="quick-edit-row">
                <label>Current Chapter:</label>
                <div className="increment-field">
                  {canModify ? (
                    <>
                      <button
                        className="increment-btn"
                        onClick={() => handleIncrementField('current_chapter', -1, hasEditableSeasons)}
                      >
                        -
                      </button>
                      <span>{currentData.current_chapter || 0}</span>
                      <button
                        className="increment-btn"
                        onClick={() => handleIncrementField('current_chapter', 1, hasEditableSeasons)}
                      >
                        +
                      </button>
                    </>
                  ) : (
                    <span>{currentData.current_chapter || 0}</span>
                  )}
                </div>
              </div>
              <div className="quick-edit-row">
                <label>Current Volume:</label>
                <div className="increment-field">
                  {canModify ? (
                    <>
                      <button
                        className="increment-btn"
                        onClick={() => handleIncrementField('current_volume', -1, hasEditableSeasons)}
                      >
                        -
                      </button>
                      <span>{currentData.current_volume || 0}</span>
                      <button
                        className="increment-btn"
                        onClick={() => handleIncrementField('current_volume', 1, hasEditableSeasons)}
                      >
                        +
                      </button>
                    </>
                  ) : (
                    <span>{currentData.current_volume || 0}</span>
                  )}
                </div>
              </div>
            </>
          ) : mediaItem.type === 'Book' ? (
            <div className="quick-edit-row">
              <label>Current Page:</label>
              <div className="increment-field">
                {canModify ? (
                  <>
                    <button
                      className="increment-btn"
                      onClick={() => handleIncrementField('current_page', -1)}
                    >
                      -
                    </button>
                    <span>{currentData.current_page || 0}{currentData.page_count ? ` / ${currentData.page_count}` : ''}</span>
                    <button
                      className="increment-btn"
                      onClick={() => handleIncrementField('current_page', 1)}
                    >
                      +
                    </button>
                  </>
                ) : (
                  <span>{currentData.current_page || 0}{currentData.page_count ? ` / ${currentData.page_count}` : ''}</span>
                )}
              </div>
            </div>
          ) : (
            <div className="quick-edit-row">
              <label>Current Episode:</label>
              <div className="increment-field">
                {canModify ? (
                  <>
                    <button
                      className="increment-btn"
                      onClick={() => handleIncrementField('current_episode', -1, hasEditableSeasons)}
                    >
                      -
                    </button>
                    <span>{currentData.current_episode || 0} / {currentData.episode_count || '?'}</span>
                    <button
                      className="increment-btn"
                      onClick={() => handleIncrementField('current_episode', 1, hasEditableSeasons)}
                    >
                      +
                    </button>
                  </>
                ) : (
                  <span>{currentData.current_episode || 0} / {currentData.episode_count || '?'}</span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Season Score for Anime with seasons (above divider) */}
      {hasEditableSeasons && (
        <div className="quick-edit-row">
          <label>Season Score:</label>
          {canModify ? (
            <input
              type="number"
              min="0"
              max="10"
              step="0.1"
              value={selectedSeason?.season_score || ''}
              onChange={(e) => handleFieldChange('season_score', e.target.value, true)}
            />
          ) : (
            <span>{selectedSeason?.season_score || 'Not Set'}</span>
          )}
          {hasEditableSeasons && <hr className="divider" />}
        </div>
      )}

      {/* Ownership and Volumes Owned */}
      {['Manga', 'Book', 'PhysicalGame', 'PSPGame', 'NDSGame', 'GameboyGame', 'SwitchGame', 'PS3Game', 'VitaGame', 'Music', 'Vinyl', 'CD'].includes(mediaItem.type) && (
        <>
          <div className="quick-edit-row">
            <label>Ownership:</label>
            {canModify ? (
              <select
                value={editedMedia.ownership_status || 'Not Owned'}
                onChange={(e) => handleFieldChange('ownership_status', e.target.value)}
              >
                {getOwnershipOptions(mediaItem.type).map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : (
              <span>{editedMedia.ownership_status || 'Not Owned'}</span>
            )}
          </div>
          {mediaItem.type === 'Manga' && (
            <div className="quick-edit-row">
              <label>Volumes Owned:</label>
              <div className="increment-field">
                {canModify ? (
                  <>
                    <button
                      className="increment-btn"
                      onClick={() => handleIncrementField('owned_volume_count', -1)}
                    >
                      -
                    </button>
                    <span>{editedMedia.owned_volume_count || 0} / {editedMedia.volume_count || '?'}</span>
                    <button
                      className="increment-btn"
                      onClick={() => handleIncrementField('owned_volume_count', 1)}
                    >
                      +
                    </button>
                  </>
                ) : (
                  <span>{editedMedia.owned_volume_count || 0} / {editedMedia.volume_count || '?'}</span>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Condition */}
      {['Manga', 'Book', 'PhysicalGame', 'PSPGame', 'NDSGame', 'GameboyGame', 'SwitchGame', 'PS3Game', 'VitaGame', 'Music', 'Vinyl', 'CD'].includes(mediaItem.type) && (
        <div className="quick-edit-row">
          <label>Condition:</label>
          {canModify ? (
            <select
              value={editedMedia.condition || ''}
              onChange={(e) => handleFieldChange('condition', e.target.value)}
            >
              <option value="">Select Condition</option>
              {['Excellent', 'Good', 'Okay', 'Bad', 'Poor'].map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          ) : (
            <span>{editedMedia.condition || 'Not Set'}</span>
          )}
        </div>
      )}

      {/* Global Score for mediaItem */}
      <div className="quick-edit-row">
        <label>Score:</label>
        {canModify ? (
          <input
            type="number"
            min="0"
            max="10"
            step="0.1"
            value={editedMedia.score || ''}
            onChange={(e) => handleFieldChange('score', e.target.value)}
          />
        ) : (
          <span>{editedMedia.score || 'Not Set'}</span>
        )}
      </div>

      {/* Local / Global Rank (only meaningful once a score exists) */}
      {typeof mediaItem.local_rank === 'number' && (
        <div className="quick-edit-row">
          <label>Local Rank:</label>
          {canModify ? (
            <input
              type="number"
              min="1"
              max={mediaItem.local_total || undefined}
              value={localRankInput}
              disabled={rankSaving}
              onChange={(e) => setLocalRankInput(e.target.value)}
              onBlur={() => {
                const pos = parseInt(localRankInput, 10);
                if (pos && pos !== mediaItem.local_rank) submitRank('local', pos);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            />
          ) : (
            <span>#{mediaItem.local_rank}</span>
          )}
          <span className="quick-edit-rank-total"> / {mediaItem.local_total}</span>
        </div>
      )}
      {typeof mediaItem.global_rank === 'number' && (
        <div className="quick-edit-row">
          <label>Global Rank:</label>
          {canModify ? (
            <input
              type="number"
              min="1"
              max={mediaItem.global_total || undefined}
              value={globalRankInput}
              disabled={rankSaving}
              onChange={(e) => setGlobalRankInput(e.target.value)}
              onBlur={() => {
                const pos = parseInt(globalRankInput, 10);
                if (pos && pos !== mediaItem.global_rank) submitRank('global', pos);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            />
          ) : (
            <span>#{mediaItem.global_rank}</span>
          )}
          <span className="quick-edit-rank-total"> / {mediaItem.global_total}</span>
        </div>
      )}
      <div className="quick-edit-row">
        <label>Privacy:</label>
        {canModify ? (
          <select
            value={editedMedia.private || '0'}
            onChange={(e) => handleFieldChange('private', e.target.value)}
          >
            <option value="0">Public</option>
            <option value="1">Logged in users only</option>
            <option value="2">Allowed users only</option>
            <option value="3">Only visible to me</option>
            <option value="4">Hidden</option>
          </select>
        ) : (
          <span>
            {editedMedia.private === '0' ? 'Public' :
             editedMedia.private === '1' ? 'Logged in users only' :
             editedMedia.private === '2' ? 'Allowed users only' :
             editedMedia.private === '3' ? 'Only visible to me' :
             editedMedia.private === '4' ? 'Hidden' : 'Not Set'}
          </span>
        )}
      </div>
    </section>
    <Modal
      isOpen={!!rankConflict}
      onClose={declineRankConflict}
      title="Rank Conflict"
      message={
        rankConflict
          ? rankConflict.suggestionKind === 'score'
            ? `Moving this to that position would put it out of score order next to "${rankConflict.neighborTitle}". Change this item's score to ${rankConflict.suggestedValue} to match?`
            : `Moving this to that global position would break its local rank order next to "${rankConflict.neighborTitle}". Change this item's local rank to ${rankConflict.suggestedValue} instead?`
          : ''
      }
      buttons={[
        { label: 'Cancel', class: 'btn btn-cancel', action: declineRankConflict },
        { label: 'Confirm', class: 'btn btn-confirm', action: acceptRankConflict },
      ]}
    />
    </>
  );
};

export default QuickEditDetails;